import { useEffect, useRef, useState, type FormEvent } from 'react';

import { PasswordField, PremiumField, Spinner } from '@kilat/ui';

import { useAuth, type GoogleSignInOutcome } from '../lib/use-auth.js';
import { passwordFieldLabels } from '../lib/form-labels.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { logger } from '../lib/logger.js';
import { loginErrorKey, resetErrorKey } from '../lib/login-errors.js';
import { marketingSignupUrl } from '../lib/marketing-origin.js';

/** The account-link branch of a Google attempt — email + pending credential. */
type LinkState = Extract<GoogleSignInOutcome, { status: 'link-with-password' }>;

/**
 * Returning-user login for the operator + super-admin dashboard.
 *
 * Rendered by `HomeRoute` whenever `!user` — it REPLACES the old dead-end
 * `SignedOut` placeholder. Email+password → `signInWithEmail`; on success the
 * existing `onAuthStateChanged`/`onIdTokenChanged` flow in `AuthProvider` takes
 * over and routing renders per claims. Works identically for operators and
 * super-admins (the claim gates what they see, not how they sign in).
 *
 * Every state is explicit and copy is custom-crafted per surface — idle,
 * submitting (spinner + specific label), and each mapped Firebase error →
 * a human line ([[feedback-no-dead-ends]], [[feedback-custom-crafted-ui-text]]).
 * The token handoff (marketing → dashboard, ADR-0015) is untouched and still
 * lives in `AuthProvider`; this is the way back in AFTER that first session.
 */
export function SignInScreen() {
  const brand = usePlatformBrand();
  const [mode, setMode] = useState<'signIn' | 'reset'>('signIn');

  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        alignItems: 'center',
        justifyItems: 'center',
        boxSizing: 'border-box',
        padding:
          'max(env(safe-area-inset-top,0px),24px) 20px max(env(safe-area-inset-bottom,0px),24px)',
        background:
          'radial-gradient(70% 60% at 90% -10%, rgba(13,122,79,0.14), transparent 60%),' +
          'radial-gradient(60% 50% at -10% 10%, rgba(245,158,11,0.10), transparent 60%),' +
          'var(--color-surface-sunken, #fafaf7)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 420 }}>
        <BrandMark name={brand.shortName} />
        {mode === 'signIn' ? (
          <SignInCard onForgot={() => setMode('reset')} />
        ) : (
          <ResetCard onBack={() => setMode('signIn')} />
        )}
      </div>
    </main>
  );
}

// ── Brand mark ──────────────────────────────────────────────────────────

function BrandMark({ name }: { readonly name: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        marginBottom: 'var(--space-5, 20px)',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 32,
          height: 32,
          borderRadius: 10,
          background: 'linear-gradient(135deg, var(--color-primary, #0d7a4f), #0f8659)',
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          fontWeight: 700,
          fontSize: 16,
          boxShadow: 'var(--shadow-2, 0 8px 20px rgba(13,122,79,0.28))',
        }}
      >
        {name.slice(0, 1)}
      </span>
      <span style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-on-surface, #0f172a)' }}>
        {name}
      </span>
    </div>
  );
}

// ── Sign-in card ────────────────────────────────────────────────────────

function SignInCard({ onForgot }: { readonly onForgot: () => void }) {
  const { t } = useI18n();
  const brand = usePlatformBrand();
  const { signInWithEmail, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [link, setLink] = useState<LinkState | null>(null);

  async function handleGoogle(): Promise<void> {
    setErrorKey(null);
    setGoogleBusy(true);
    const outcome = await signInWithGoogle();
    if (outcome.status === 'success') return; // unmounts on the auth-state flip
    if (outcome.status === 'link-with-password') {
      // The email already has a password account → collect it once and link,
      // rather than dead-ending on the Firebase conflict.
      setLink(outcome);
      setGoogleBusy(false);
      return;
    }
    if (outcome.errorKey) setErrorKey(outcome.errorKey); // null = benign popup cancel
    setGoogleBusy(false);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setErrorKey(null);
    setSubmitting(true);
    try {
      await signInWithEmail(email.trim(), password);
      // Success: AuthProvider's onAuthStateChanged flips `user`, HomeRoute
      // re-renders and this component unmounts — no manual navigation needed.
    } catch (err) {
      // Map every failure to ONE generic, non-leaking line. We deliberately do
      // NOT probe `fetchSignInMethodsForEmail` on the failure path: telling an
      // unauthenticated guesser "this email signs in with Google" would leak
      // whether an email is a real Kilat operator/super-admin account — an
      // account-enumeration oracle (security-review 2026-07-22). The legitimate
      // Google→password LINK flow still knows the email is Google-linked, but
      // only because Firebase itself told it (the
      // `account-exists-with-different-credential` branch in use-auth.tsx).
      const key = loginErrorKey(err);
      setErrorKey(key);
      logger.warn('dashboard sign-in failed', { key });
      setSubmitting(false);
    }
  }

  if (link) {
    return (
      <LinkPanel
        link={link}
        onBack={() => {
          setLink(null);
          setErrorKey(null);
        }}
      />
    );
  }

  return (
    <Card>
      <CardHeader
        eyebrow={t('auth.login.eyebrow')}
        title={t('auth.login.title', { brand: brand.name })}
      >
        {t('auth.login.subtitle')}
      </CardHeader>

      <GoogleButton busy={googleBusy} onClick={handleGoogle}>
        {googleBusy ? t('auth.login.googleSubmitting') : t('auth.login.google')}
      </GoogleButton>

      <OrDivider label={t('auth.login.orEmail')} />

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 'var(--space-4, 16px)' }}>
        <PremiumField
          label={t('auth.login.emailLabel')}
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          placeholder={t('auth.login.emailPlaceholder')}
          required
          icon="mail"
        />
        <div style={{ display: 'grid', gap: 8 }}>
          <PasswordField
            labels={passwordFieldLabels(t)}
            label={t('auth.login.passwordLabel')}
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            placeholder={t('auth.login.passwordPlaceholder')}
            required
          />
          <button
            type="button"
            onClick={onForgot}
            style={linkButtonStyle}
            data-testid="forgot-link"
          >
            {t('auth.login.forgot')}
          </button>
        </div>

        {errorKey ? <ErrorAlert>{t(errorKey)}</ErrorAlert> : null}

        <SubmitButton submitting={submitting}>
          {submitting ? t('auth.login.submitting') : t('auth.login.submit')}
        </SubmitButton>
      </form>

      <Divider />

      <p style={footerLineStyle}>
        {t('auth.login.noAccount', { brand: brand.name })}{' '}
        <a href={marketingSignupUrl()} style={inlineLinkStyle}>
          {t('auth.login.createAccount')}
        </a>
      </p>
    </Card>
  );
}

// ── Google link panel (account-model resolution) ─────────────────────────

/**
 * Shown when "Continue with Google" hit `account-exists-with-different-credential`
 * — i.e. the operator already signs in with a password. They enter that password
 * ONCE; we sign in and `linkWithCredential` attaches Google, so every future tap
 * is one-touch. This is the correct fix for the reason Google was first deferred.
 */
function LinkPanel({ link, onBack }: { readonly link: LinkState; readonly onBack: () => void }) {
  const { t } = useI18n();
  const { linkGoogleAfterPassword } = useAuth();
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  // `linkGoogleAfterPassword` signs in FIRST (to prove the password) and only
  // THEN attaches Google. The sign-in flips auth state, which can unmount this
  // panel before `linkWithCredential` settles. Guard every post-await setState
  // so a link failure landing after unmount doesn't setState on a dead
  // component (React warning + a silently-dropped Google attach). If we're
  // still mounted we surface the error; if not, the user is already signed in
  // and the best-effort Google link simply didn't complete this time.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setErrorKey(null);
    setSubmitting(true);
    try {
      await linkGoogleAfterPassword(link.email, password, link.pendingCredential);
      // Success unmounts via the auth-state flip.
    } catch (err) {
      const key = loginErrorKey(err);
      logger.warn('google account-link failed', { key });
      if (!mountedRef.current) return;
      setErrorKey(key);
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader eyebrow={t('auth.login.eyebrow')} title={t('auth.login.linkTitle')}>
        {t('auth.login.linkBody', { email: link.email })}
      </CardHeader>

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 'var(--space-4, 16px)' }}>
        <PasswordField
          labels={passwordFieldLabels(t)}
          label={t('auth.login.passwordLabel')}
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          placeholder={t('auth.login.passwordPlaceholder')}
          required
        />

        {errorKey ? <ErrorAlert>{t(errorKey)}</ErrorAlert> : null}

        <SubmitButton submitting={submitting}>
          {submitting ? t('auth.login.linkSubmitting') : t('auth.login.linkSubmit')}
        </SubmitButton>
      </form>

      <button
        type="button"
        onClick={onBack}
        style={{ ...linkButtonStyle, justifySelf: 'center', marginTop: 'var(--space-4, 16px)' }}
      >
        ← {t('auth.login.linkBack')}
      </button>
    </Card>
  );
}

// ── Google button + glyph ────────────────────────────────────────────────

function GoogleButton({
  busy,
  onClick,
  children,
}: {
  readonly busy: boolean;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        width: '100%',
        minHeight: 'var(--touch-target, 44px)',
        padding: '13px 18px',
        borderRadius: 'var(--radius-md, 14px)',
        border: '1px solid var(--color-border-strong)',
        background: 'var(--color-surface, #ffffff)',
        color: 'var(--color-on-surface, #0f172a)',
        fontFamily: 'inherit',
        fontSize: 'var(--text-body, 15px)',
        fontWeight: 600,
        cursor: busy ? 'wait' : 'pointer',
        opacity: busy ? 0.75 : 1,
        boxShadow: '0 1px 2px var(--tint-subtle)',
      }}
    >
      {busy ? <Spinner size={18} color="#64748b" /> : <GoogleGlyph />}
      {children}
    </button>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}

function OrDivider({ label }: { readonly label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        margin: 'var(--space-5, 20px) 0',
        color: 'var(--color-on-surface-muted, #94a3b8)',
      }}
    >
      <span aria-hidden style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
      <span style={{ fontSize: 'var(--text-meta, 12px)', fontWeight: 500 }}>{label}</span>
      <span aria-hidden style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
    </div>
  );
}

// ── No-operator-account screen (signed in, but no tenant/super-admin) ─────

/**
 * Rendered by `HomeRoute` when someone authenticates (Google OR email) but has
 * NO operator role and NO super-admin claim — e.g. a brand-new Google user who
 * never signed up. It must NOT dead-end ([[feedback-no-dead-ends]]): it names
 * the situation and gives two real next actions — go start a carwash, or sign
 * out and use the email they actually registered with.
 */
export function NoOperatorAccount() {
  const { t } = useI18n();
  const brand = usePlatformBrand();
  const { user, signOut } = useAuth();
  const email = user?.email ?? '';

  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        alignItems: 'center',
        justifyItems: 'center',
        boxSizing: 'border-box',
        padding:
          'max(env(safe-area-inset-top,0px),24px) 20px max(env(safe-area-inset-bottom,0px),24px)',
        background: 'var(--color-surface-sunken, #fafaf7)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 420 }}>
        <BrandMark name={brand.shortName} />
        <Card>
          <CardHeader eyebrow={t('auth.login.eyebrow')} title={t('auth.login.noOperatorTitle')}>
            {t('auth.login.noOperatorBody', { email })}
          </CardHeader>

          <a
            href={marketingSignupUrl()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              minHeight: 'var(--touch-target, 44px)',
              padding: '15px 22px',
              borderRadius: 'var(--radius-md, 14px)',
              background: 'linear-gradient(135deg, var(--color-primary, #0d7a4f) 0%, #0f8659 100%)',
              color: 'var(--color-primary-fg, #ffffff)',
              fontWeight: 600,
              fontSize: 'var(--text-body, 15px)',
              textDecoration: 'none',
              boxShadow: '0 1px 2px rgba(13,122,79,0.4), 0 8px 20px rgba(13,122,79,0.24)',
              boxSizing: 'border-box',
            }}
          >
            {t('auth.login.noOperatorCta')}
          </a>

          <button
            type="button"
            onClick={() => void signOut()}
            style={{ ...linkButtonStyle, justifySelf: 'center', marginTop: 'var(--space-5, 20px)' }}
          >
            {t('auth.login.noOperatorSwitch')}
          </button>
        </Card>
      </div>
    </main>
  );
}

// ── Reset card ──────────────────────────────────────────────────────────

function ResetCard({ onBack }: { readonly onBack: () => void }) {
  const { t } = useI18n();
  const { sendPasswordReset } = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setErrorKey(null);
    setSubmitting(true);
    try {
      await sendPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      // `resetErrorKey` returns null for unknown-email → we STILL show the
      // confirmation so the form never reveals whether an account exists.
      const key = resetErrorKey(err);
      if (key === null) {
        setSent(true);
      } else {
        setErrorKey(key);
        logger.warn('dashboard password-reset failed', { key });
        setSubmitting(false);
      }
    }
  }

  if (sent) {
    return (
      <Card>
        <CardHeader eyebrow={t('auth.login.eyebrow')} title={t('auth.login.resetSentTitle')}>
          {t('auth.login.resetSentBody', { email: email.trim() })}
        </CardHeader>
        <button type="button" onClick={onBack} style={{ ...linkButtonStyle, justifySelf: 'start' }}>
          ← {t('auth.login.resetBack')}
        </button>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader eyebrow={t('auth.login.eyebrow')} title={t('auth.login.resetTitle')}>
        {t('auth.login.resetSubtitle')}
      </CardHeader>

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 'var(--space-4, 16px)' }}>
        <PremiumField
          label={t('auth.login.emailLabel')}
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          placeholder={t('auth.login.emailPlaceholder')}
          autoFocus
          required
          icon="mail"
        />

        {errorKey ? <ErrorAlert>{t(errorKey)}</ErrorAlert> : null}

        <SubmitButton submitting={submitting}>
          {submitting ? t('auth.login.resetSubmitting') : t('auth.login.resetSubmit')}
        </SubmitButton>
      </form>

      <button
        type="button"
        onClick={onBack}
        style={{ ...linkButtonStyle, justifySelf: 'center', marginTop: 'var(--space-4, 16px)' }}
      >
        ← {t('auth.login.resetBack')}
      </button>
    </Card>
  );
}

// ── Shared building blocks ──────────────────────────────────────────────

function Card({ children }: { readonly children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--color-surface, #ffffff)',
        borderRadius: 'var(--radius-lg, 20px)',
        border: '1px solid var(--color-border)',
        padding: 'var(--space-6, 24px)',
        boxShadow:
          '0 1px 2px var(--tint-subtle), 0 8px 24px var(--color-border), 0 24px 60px rgba(13,122,79,0.05)',
      }}
    >
      {children}
    </div>
  );
}

function CardHeader({
  eyebrow,
  title,
  children,
}: {
  readonly eyebrow: string;
  readonly title: string;
  readonly children: React.ReactNode;
}) {
  return (
    <header style={{ marginBottom: 'var(--space-5, 20px)' }}>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta, 12px)',
          fontWeight: 600,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: 'var(--color-primary, #0d7a4f)',
        }}
      >
        {eyebrow}
      </p>
      <h1
        style={{
          margin: 'var(--space-2, 8px) 0 0 0',
          fontSize: 'var(--text-h2, 24px)',
          fontWeight: 600,
          color: 'var(--color-on-surface, #0f172a)',
        }}
      >
        {title}
      </h1>
      <p
        style={{
          margin: 'var(--space-2, 8px) 0 0 0',
          fontSize: 'var(--text-body, 15px)',
          lineHeight: 1.5,
          color: 'var(--color-on-surface-muted, #64748b)',
        }}
      >
        {children}
      </p>
    </header>
  );
}

function SubmitButton({
  submitting,
  children,
}: {
  readonly submitting: boolean;
  readonly children: React.ReactNode;
}) {
  return (
    <button
      type="submit"
      disabled={submitting}
      style={{
        marginTop: 'var(--space-1, 4px)',
        background: submitting
          ? 'var(--color-primary, #0d7a4f)'
          : 'linear-gradient(135deg, var(--color-primary, #0d7a4f) 0%, #0f8659 100%)',
        color: 'var(--color-primary-fg, #ffffff)',
        padding: '15px 22px',
        borderRadius: 'var(--radius-md, 14px)',
        fontWeight: 600,
        fontSize: 'var(--text-body, 15px)',
        fontFamily: 'inherit',
        border: 'none',
        cursor: submitting ? 'wait' : 'pointer',
        opacity: submitting ? 0.85 : 1,
        boxShadow: '0 1px 2px rgba(13,122,79,0.4), 0 8px 20px rgba(13,122,79,0.24)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        minHeight: 'var(--touch-target, 44px)',
      }}
    >
      {submitting ? <Spinner /> : null}
      {children}
    </button>
  );
}

function ErrorAlert({ children }: { readonly children: React.ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        padding: '10px 14px',
        borderRadius: 'var(--radius-sm, 12px)',
        background: 'rgba(239,68,68,0.06)',
        border: '1px solid rgba(239,68,68,0.22)',
        color: 'var(--color-tone-error, #991b1b)',
        fontSize: 'var(--text-meta, 13px)',
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

function Divider() {
  return (
    <div
      aria-hidden
      style={{
        height: 1,
        background: 'var(--color-border)',
        margin: 'var(--space-5, 20px) 0',
      }}
    />
  );
}

const linkButtonStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  color: 'var(--color-primary, #0d7a4f)',
  fontSize: 'var(--text-meta, 13px)',
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
  justifySelf: 'end',
  textAlign: 'right',
};

const inlineLinkStyle: React.CSSProperties = {
  color: 'var(--color-primary, #0d7a4f)',
  fontWeight: 600,
  textDecoration: 'none',
};

const footerLineStyle: React.CSSProperties = {
  margin: 0,
  textAlign: 'center',
  fontSize: 'var(--text-meta, 13px)',
  color: 'var(--color-on-surface-muted, #64748b)',
};
